import { useEffect, useState } from "react";
import { io } from "socket.io-client";
import config from "@/lib/constants/config";

const socketState = {
  socket: null,
  userId: null,
  token: null,
  subscribers: 0,
};

const getSocketUrl = () => {
  const base = config.baseUrl || "";
  return base.endsWith("/") ? base.slice(0, -1) : base;
};

const ensureSocket = (userId, token) => {
  if (!userId || !token) return null;

  const shouldReconnect =
    !socketState.socket ||
    socketState.userId !== userId ||
    socketState.token !== token;

  if (!shouldReconnect) return socketState.socket;

  if (socketState.socket) {
    socketState.socket.disconnect();
  }

  const socket = io(getSocketUrl(), {
    withCredentials: true,
    query: { userId: userId?.toString?.() ?? userId },
    auth: token ? { token } : undefined,
  });

  socket.on("connect", () => {
    socket.emit("client:ping", { hello: "Hello" }, () => {});
  });

  socket.on("connect_error", (error) => {
    console.error("Socket connection error:", error?.message || error);
  });

  socketState.socket = socket;
  socketState.userId = userId;
  socketState.token = token;

  return socketState.socket;
};

const disconnectSocket = () => {
  if (!socketState.socket) return;
  socketState.socket.disconnect();
  socketState.socket = null;
  socketState.userId = null;
  socketState.token = null;
};

export const useSocket = (userId, token) => {
  const [socket, setSocket] = useState(() => socketState.socket);

  useEffect(() => {
    if (!userId || !token) {
      setSocket(null);
      return undefined;
    }

    socketState.subscribers += 1;
    const active = ensureSocket(userId, token);
    setSocket(active);

    return () => {
      socketState.subscribers = Math.max(socketState.subscribers - 1, 0);
      if (socketState.subscribers === 0) {
        disconnectSocket();
      }
    };
  }, [token, userId]);

  return socket;
};
